import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Site, FortnoxOSSSettings as FortnoxOSSSettingsType } from '@/types';
import { apiService } from '@/utils/api';

interface FortnoxOSSSettingsProps {
  className?: string;
}

// Helper component for country VAT settings - moved outside to prevent re-creation
const CountrySection: React.FC<{
  country: string;
  fields: Array<{ label: string; field: string }>;
  formData: Partial<FortnoxOSSSettingsType>;
  handleInputChange: (field: string, value: any) => void;
  isLoading: boolean;
}> = ({ country, fields, formData, handleInputChange, isLoading }) => (
  <div className="space-y-4">
    <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">
      {country}
    </h3>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {fields.map(({ label, field }) => (
        <div key={field}>
          <label className="form-label">{label}:</label>
          <input
            type="number"
            value={formData[field as keyof FortnoxOSSSettingsType] || ''}
            onChange={(e) => {
              const value = e.target.value;
              // Only allow 4-digit numbers
              if (value === '' || (value.length <= 4 && /^\d+$/.test(value))) {
                handleInputChange(field, value === '' ? '' : parseInt(value));
              }
            }}
            className="form-input"
            disabled={isLoading}
            min="1000"
            max="9999"
            placeholder="4 digits"
          />
        </div>
      ))}
    </div>
  </div>
);

const FortnoxOSSSettings: React.FC<FortnoxOSSSettingsProps> = ({ className }) => {
  const queryClient = useQueryClient();
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [formData, setFormData] = useState<Partial<FortnoxOSSSettingsType>>({});
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  // Fetch sites
  const { data: sites = [], isLoading: sitesLoading } = useQuery(
    ['sites'],
    () => apiService.getSites()
  );

  // Set default site when sites are loaded
  useEffect(() => {
    if (sites.length > 0 && !selectedSiteId) {
      setSelectedSiteId(sites[0].id);
    }
  }, [sites, selectedSiteId]);

  // Fetch Fortnox OSS settings
  const { data: ossSettings, isLoading: settingsLoading } = useQuery(
    ['fortnox-oss-settings', selectedSiteId],
    () => apiService.getFortnoxOSSSettings(selectedSiteId!),
    {
      enabled: !!selectedSiteId,
    }
  );

  // Update form data when settings are loaded
  useEffect(() => {
    if (ossSettings) {
      setFormData(ossSettings);
    }
  }, [ossSettings]);

  // Save mutation
  const saveMutation = useMutation(
    async (data: FortnoxOSSSettingsType) => {
      if (data.id) {
        return apiService.updateFortnoxOSSSettings(selectedSiteId!, data);
      } else {
        return apiService.createFortnoxOSSSettings(selectedSiteId!, data);
      }
    },
    {
      onSuccess: () => {
        setMessage({ type: 'success', text: 'Fortnox OSS Settings saved' });
        queryClient.invalidateQueries(['fortnox-oss-settings']);
        setTimeout(() => setMessage(null), 3000);
      },
      onError: () => {
        setMessage({ type: 'error', text: 'Something went wrong' });
        setTimeout(() => setMessage(null), 3000);
      },
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    async () => {
      return apiService.deleteFortnoxOSSSettings(selectedSiteId!);
    },
    {
      onSuccess: () => {
        setMessage({ type: 'success', text: 'Fortnox OSS Settings deleted' });
        setFormData({});
        queryClient.invalidateQueries(['fortnox-oss-settings']);
        setTimeout(() => setMessage(null), 3000);
      },
      onError: () => {
        setMessage({ type: 'error', text: 'Something went wrong' });
        setTimeout(() => setMessage(null), 3000);
      },
    }
  );

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSiteChange = (siteId: number) => {
    setSelectedSiteId(siteId);
    setFormData({});
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSiteId) return;
    
    const submitData: FortnoxOSSSettingsType = {
      ...formData,
      site_id: selectedSiteId,
    } as FortnoxOSSSettingsType;
    
    saveMutation.mutate(submitData);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this integration?')) {
      deleteMutation.mutate();
    }
  };

  const isLoading = sitesLoading || settingsLoading;

  return (
    <div className={`container mx-auto px-4 py-8 ${className || ''}`}>
      <h1 className="section-title">Fortnox OSS Settings</h1>
      
      {/* Loading Alert */}
      {isLoading && (
        <div className="alert alert-info">
          <span>Loading...</span>
        </div>
      )}

      {/* Success/Error Messages */}
      {message && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : message.type === 'error' ? 'alert-danger' : 'alert-info'}`}>
          <span>{message.text}</span>
        </div>
      )}

      <div className="card">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Site Selection */}
          <div>
            <label className="form-label">Site ID:</label>
            <select
              value={selectedSiteId || ''}
              onChange={(e) => handleSiteChange(parseInt(e.target.value))}
              className="form-input"
              disabled={isLoading}
            >
              {sites.map((site: Site) => (
                <option key={site.id} value={site.id}>
                  {site.url}
                </option>
              ))}
            </select>
          </div>

          {/* Country VAT Settings */}
          <div className="space-y-8">
            <CountrySection
              country="Austria"
              fields={[
                { label: 'Sales 20% VAT Account', field: 'sales_AT_vat_20_account' },
                { label: 'Sales 13% VAT Account', field: 'sales_AT_vat_13_account' },
                { label: 'Sales 10% VAT Account', field: 'sales_AT_vat_10_account' },
                { label: '20% VAT Account', field: 'AT_vat_20_account' },
                { label: '13% VAT Account', field: 'AT_vat_13_account' },
                { label: '10% VAT Account', field: 'AT_vat_10_account' },
              ]}
              formData={formData}
              handleInputChange={handleInputChange}
              isLoading={isLoading}
            />

            <CountrySection
              country="Belgium"
              fields={[
                { label: 'Sales 21% VAT Account', field: 'sales_BE_vat_21_account' },
                { label: 'Sales 12% VAT Account', field: 'sales_BE_vat_12_account' },
                { label: 'Sales 6% VAT Account', field: 'sales_BE_vat_6_account' },
                { label: '21% VAT Account', field: 'BE_vat_21_account' },
                { label: '12% VAT Account', field: 'BE_vat_12_account' },
                { label: '6% VAT Account', field: 'BE_vat_6_account' },
              ]}
              formData={formData}
              handleInputChange={handleInputChange}
              isLoading={isLoading}
            />

            <CountrySection
              country="Bulgaria"
              fields={[
                { label: 'Sales 20% VAT Account', field: 'sales_BG_vat_20_account' },
                { label: 'Sales 9% VAT Account', field: 'sales_BG_vat_9_account' },
                { label: '20% VAT Account', field: 'BG_vat_20_account' },
                { label: '9% VAT Account', field: 'BG_vat_9_account' },
              ]}
              formData={formData}
              handleInputChange={handleInputChange}
              isLoading={isLoading}
            />

            <CountrySection
              country="Croatia"
              fields={[
                { label: 'Sales 25% VAT Account', field: 'sales_HR_vat_25_account' },
                { label: 'Sales 13% VAT Account', field: 'sales_HR_vat_13_account' },
                { label: 'Sales 5% VAT Account', field: 'sales_HR_vat_5_account' },
                { label: '25% VAT Account', field: 'HR_vat_25_account' },
                { label: '13% VAT Account', field: 'HR_vat_13_account' },
                { label: '5% VAT Account', field: 'HR_vat_5_account' },
              ]}
              formData={formData}
              handleInputChange={handleInputChange}
              isLoading={isLoading}
            />

            <CountrySection
              country="Cyprus"
              fields={[
                { label: 'Sales 19% VAT Account', field: 'sales_CY_vat_19_account' },
                { label: 'Sales 9% VAT Account', field: 'sales_CY_vat_9_account' },
                { label: 'Sales 5% VAT Account', field: 'sales_CY_vat_5_account' },
                { label: '19% VAT Account', field: 'CY_vat_19_account' },
                { label: '9% VAT Account', field: 'CY_vat_9_account' },
                { label: '5% VAT Account', field: 'CY_vat_5_account' },
              ]}
              formData={formData}
              handleInputChange={handleInputChange}
              isLoading={isLoading}
            />

            <CountrySection
              country="Czech Republic"
              fields={[
                { label: 'Sales 21% VAT Account', field: 'sales_CZ_vat_21_account' },
                { label: 'Sales 15% VAT Account', field: 'sales_CZ_vat_15_account' },
                { label: 'Sales 10% VAT Account', field: 'sales_CZ_vat_10_account' },
                { label: '21% VAT Account', field: 'CZ_vat_21_account' },
                { label: '15% VAT Account', field: 'CZ_vat_15_account' },
                { label: '10% VAT Account', field: 'CZ_vat_10_account' },
              ]}
              formData={formData}
              handleInputChange={handleInputChange}
              isLoading={isLoading}
            />

            <CountrySection
              country="Denmark"
              fields={[
                { label: 'Sales 25% VAT Account', field: 'sales_DK_vat_25_account' },
                { label: '25% VAT Account', field: 'DK_vat_25_account' },
              ]}
              formData={formData}
              handleInputChange={handleInputChange}
              isLoading={isLoading}
            />

            <CountrySection
              country="Germany"
              fields={[
                { label: 'Sales 19% VAT Account', field: 'sales_DE_vat_19_account' },
                { label: 'Sales 7% VAT Account', field: 'sales_DE_vat_7_account' },
                { label: '19% VAT Account', field: 'DE_vat_19_account' },
                { label: '7% VAT Account', field: 'DE_vat_7_account' },
              ]}
              formData={formData}
              handleInputChange={handleInputChange}
              isLoading={isLoading}
            />

            <CountrySection
              country="Estonia"
              fields={[
                { label: 'Sales 20% VAT Account', field: 'sales_EE_vat_20_account' },
                { label: 'Sales 9% VAT Account', field: 'sales_EE_vat_9_account' },
                { label: '20% VAT Account', field: 'EE_vat_20_account' },
                { label: '9% VAT Account', field: 'EE_vat_9_account' },
              ]}
              formData={formData}
              handleInputChange={handleInputChange}
              isLoading={isLoading}
            />

            <CountrySection
              country="Finland"
              fields={[
                { label: 'Sales 24% VAT Account', field: 'sales_FI_vat_255_account' },
                { label: 'Sales 14% VAT Account', field: 'sales_FI_vat_14_account' },
                { label: 'Sales 10% VAT Account', field: 'sales_FI_vat_10_account' },
                { label: '24% VAT Account', field: 'FI_vat_255_account' },
                { label: '14% VAT Account', field: 'FI_vat_14_account' },
                { label: '10% VAT Account', field: 'FI_vat_10_account' },
              ]}
              formData={formData}
              handleInputChange={handleInputChange}
              isLoading={isLoading}
            />

            <CountrySection
              country="France"
              fields={[
                { label: 'Sales 20% VAT Account', field: 'sales_FR_vat_20_account' },
                { label: 'Sales 10% VAT Account', field: 'sales_FR_vat_10_account' },
                { label: '20% VAT Account', field: 'FR_vat_20_account' },
                { label: '10% VAT Account', field: 'FR_vat_10_account' },
              ]}
              formData={formData}
              handleInputChange={handleInputChange}
              isLoading={isLoading}
            />

            <CountrySection
              country="Greece"
              fields={[
                { label: 'Sales 24% VAT Account', field: 'sales_GR_vat_24_account' },
                { label: 'Sales 13% VAT Account', field: 'sales_GR_vat_13_account' },
                { label: 'Sales 6% VAT Account', field: 'sales_GR_vat_6_account' },
                { label: '24% VAT Account', field: 'GR_vat_24_account' },
                { label: '13% VAT Account', field: 'GR_vat_13_account' },
                { label: '6% VAT Account', field: 'GR_vat_6_account' },
              ]}
              formData={formData}
              handleInputChange={handleInputChange}
              isLoading={isLoading}
            />

            <CountrySection
              country="Hungary"
              fields={[
                { label: 'Sales 27% VAT Account', field: 'sales_HU_vat_27_account' },
                { label: 'Sales 18% VAT Account', field: 'sales_HU_vat_18_account' },
                { label: 'Sales 5% VAT Account', field: 'sales_HU_vat_5_account' },
                { label: '27% VAT Account', field: 'HU_vat_27_account' },
                { label: '18% VAT Account', field: 'HU_vat_18_account' },
                { label: '5% VAT Account', field: 'HU_vat_5_account' },
              ]}
              formData={formData}
              handleInputChange={handleInputChange}
              isLoading={isLoading}
            />

            <CountrySection
              country="Ireland"
              fields={[
                { label: 'Sales 23% VAT Account', field: 'sales_IE_vat_23_account' },
                { label: 'Sales 9% VAT Account', field: 'sales_IE_vat_9_account' },
                { label: '23% VAT Account', field: 'IE_vat_23_account' },
                { label: '9% VAT Account', field: 'IE_vat_9_account' },
              ]}
              formData={formData}
              handleInputChange={handleInputChange}
              isLoading={isLoading}
            />

            <CountrySection
              country="Italy"
              fields={[
                { label: 'Sales 22% VAT Account', field: 'sales_IT_vat_22_account' },
                { label: 'Sales 10% VAT Account', field: 'sales_IT_vat_10_account' },
                { label: 'Sales 5% VAT Account', field: 'sales_IT_vat_5_account' },
                { label: '22% VAT Account', field: 'IT_vat_22_account' },
                { label: '10% VAT Account', field: 'IT_vat_10_account' },
                { label: '5% VAT Account', field: 'IT_vat_5_account' },
              ]}
              formData={formData}
              handleInputChange={handleInputChange}
              isLoading={isLoading}
            />

            <CountrySection
              country="Latvia"
              fields={[
                { label: 'Sales 21% VAT Account', field: 'sales_LV_vat_21_account' },
                { label: 'Sales 12% VAT Account', field: 'sales_LV_vat_12_account' },
                { label: 'Sales 5% VAT Account', field: 'sales_LV_vat_5_account' },
                { label: '21% VAT Account', field: 'LV_vat_21_account' },
                { label: '12% VAT Account', field: 'LV_vat_12_account' },
                { label: '5% VAT Account', field: 'LV_vat_5_account' },
              ]}
              formData={formData}
              handleInputChange={handleInputChange}
              isLoading={isLoading}
            />

            <CountrySection
              country="Lithuania"
              fields={[
                { label: 'Sales 21% VAT Account', field: 'sales_LT_vat_21_account' },
                { label: 'Sales 9% VAT Account', field: 'sales_LT_vat_9_account' },
                { label: 'Sales 5% VAT Account', field: 'sales_LT_vat_5_account' },
                { label: '21% VAT Account', field: 'LT_vat_21_account' },
                { label: '9% VAT Account', field: 'LT_vat_9_account' },
                { label: '5% VAT Account', field: 'LT_vat_5_account' },
              ]}
              formData={formData}
              handleInputChange={handleInputChange}
              isLoading={isLoading}
            />

            <CountrySection
              country="Luxembourg"
              fields={[
                { label: 'Sales 16% VAT Account', field: 'sales_LU_vat_16_account' },
                { label: 'Sales 14% VAT Account', field: 'sales_LU_vat_14_account' },
                { label: 'Sales 8% VAT Account', field: 'sales_LU_vat_8_account' },
                { label: 'Sales 3% VAT Account', field: 'sales_LU_vat_3_account' },
                { label: '16% VAT Account', field: 'LU_vat_16_account' },
                { label: '14% VAT Account', field: 'LU_vat_14_account' },
                { label: '8% VAT Account', field: 'LU_vat_8_account' },
                { label: '3% VAT Account', field: 'LU_vat_3_account' },
              ]}
              formData={formData}
              handleInputChange={handleInputChange}
              isLoading={isLoading}
            />

            <CountrySection
              country="Malta"
              fields={[
                { label: 'Sales 18% VAT Account', field: 'sales_MT_vat_18_account' },
                { label: 'Sales 7% VAT Account', field: 'sales_MT_vat_7_account' },
                { label: 'Sales 5% VAT Account', field: 'sales_MT_vat_5_account' },
                { label: '18% VAT Account', field: 'MT_vat_18_account' },
                { label: '7% VAT Account', field: 'MT_vat_7_account' },
                { label: '5% VAT Account', field: 'MT_vat_5_account' },
              ]}
              formData={formData}
              handleInputChange={handleInputChange}
              isLoading={isLoading}
            />

            <CountrySection
              country="Netherlands"
              fields={[
                { label: 'Sales 21% VAT Account', field: 'sales_NL_vat_21_account' },
                { label: 'Sales 9% VAT Account', field: 'sales_NL_vat_9_account' },
                { label: '21% VAT Account', field: 'NL_vat_21_account' },
                { label: '9% VAT Account', field: 'NL_vat_9_account' },
              ]}
              formData={formData}
              handleInputChange={handleInputChange}
              isLoading={isLoading}
            />

            <CountrySection
              country="Poland"
              fields={[
                { label: 'Sales 23% VAT Account', field: 'sales_PL_vat_23_account' },
                { label: 'Sales 8% VAT Account', field: 'sales_PL_vat_8_account' },
                { label: 'Sales 5% VAT Account', field: 'sales_PL_vat_5_account' },
                { label: '23% VAT Account', field: 'PL_vat_23_account' },
                { label: '8% VAT Account', field: 'PL_vat_8_account' },
                { label: '5% VAT Account', field: 'PL_vat_5_account' },
              ]}
              formData={formData}
              handleInputChange={handleInputChange}
              isLoading={isLoading}
            />

            <CountrySection
              country="Portugal"
              fields={[
                { label: 'Sales 23% VAT Account', field: 'sales_PT_vat_23_account' },
                { label: 'Sales 13% VAT Account', field: 'sales_PT_vat_13_account' },
                { label: 'Sales 6% VAT Account', field: 'sales_PT_vat_6_account' },
                { label: '23% VAT Account', field: 'PT_vat_23_account' },
                { label: '13% VAT Account', field: 'PT_vat_13_account' },
                { label: '6% VAT Account', field: 'PT_vat_6_account' },
              ]}
              formData={formData}
              handleInputChange={handleInputChange}
              isLoading={isLoading}
            />

            <CountrySection
              country="Romania"
              fields={[
                { label: 'Sales 19% VAT Account', field: 'sales_RO_vat_19_account' },
                { label: 'Sales 9% VAT Account', field: 'sales_RO_vat_9_account' },
                { label: 'Sales 5% VAT Account', field: 'sales_RO_vat_5_account' },
                { label: '19% VAT Account', field: 'RO_vat_19_account' },
                { label: '9% VAT Account', field: 'RO_vat_9_account' },
                { label: '5% VAT Account', field: 'RO_vat_5_account' },
              ]}
              formData={formData}
              handleInputChange={handleInputChange}
              isLoading={isLoading}
            />

            <CountrySection
              country="Slovakia"
              fields={[
                { label: 'Sales 20% VAT Account', field: 'sales_SK_vat_20_account' },
                { label: 'Sales 10% VAT Account', field: 'sales_SK_vat_10_account' },
                { label: '20% VAT Account', field: 'SK_vat_20_account' },
                { label: '10% VAT Account', field: 'SK_vat_10_account' },
              ]}
              formData={formData}
              handleInputChange={handleInputChange}
              isLoading={isLoading}
            />

            <CountrySection
              country="Slovenia"
              fields={[
                { label: 'Sales 22% VAT Account', field: 'sales_SI_vat_22_account' },
                { label: 'Sales 5% VAT Account', field: 'sales_SI_vat_5_account' },
                { label: '22% VAT Account', field: 'SI_vat_22_account' },
                { label: '5% VAT Account', field: 'SI_vat_5_account' },
              ]}
              formData={formData}
              handleInputChange={handleInputChange}
              isLoading={isLoading}
            />

            <CountrySection
              country="Spain"
              fields={[
                { label: 'Sales 21% VAT Account', field: 'sales_ES_vat_21_account' },
                { label: 'Sales 10% VAT Account', field: 'sales_ES_vat_10_account' },
                { label: 'Sales 4% VAT Account', field: 'sales_ES_vat_4_account' },
                { label: '21% VAT Account', field: 'ES_vat_21_account' },
                { label: '10% VAT Account', field: 'ES_vat_10_account' },
                { label: '4% VAT Account', field: 'ES_vat_4_account' },
              ]}
              formData={formData}
              handleInputChange={handleInputChange}
              isLoading={isLoading}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end pt-6 space-x-3">
            <button
              type="submit"
              className="px-4 py-2 text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50"
              style={{ backgroundColor: 'hsl(15 100% 52%)' }}
              disabled={isLoading || saveMutation.isLoading}
            >
              {saveMutation.isLoading ? 'Saving...' : 'Save'}
            </button>
            
            {formData.id && (
              <button
                type="button"
                onClick={handleDelete}
                className="btn btn-danger"
                disabled={isLoading || deleteMutation.isLoading}
              >
                {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default FortnoxOSSSettings;
